// src/pages/Account.jsx
import React, { useEffect, useState } from "react";
import axios from "../services/api";
import { useNavigate, Link } from "react-router-dom";

function Account() {
  const [user, setUser] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);
  const [error, setError] = useState("");

  const [freeTicketUsage, setFreeTicketUsage] = useState(null);

  // Tickets
  const [tickets, setTickets] = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(true);
  const [ticketsError, setTicketsError] = useState("");
  const [showPast, setShowPast] = useState(false);

  // Affiliate
  const [affiliateLoading, setAffiliateLoading] = useState(false);

  // Delete/anonymise
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [acknowledge, setAcknowledge] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  const navigate = useNavigate();

  useEffect(() => {
    fetchUserInfo();
    fetchFreeTicketUsage();
  }, []);

  useEffect(() => {
    fetchTickets();
  }, [showPast]);

  const fetchUserInfo = async () => {
    try {
      const res = await axios.get("/api/account/me");
      setUser(res.data);
    } catch {
      setError("Failed to load account details.");
    } finally {
      setLoadingUser(false);
    }
  };

  const fetchFreeTicketUsage = async () => {
    try {
      const res = await axios.get("/api/account/free-tickets");
      setFreeTicketUsage(res.data);
    } catch {}
  };

  const fetchTickets = async () => {
    setTicketsLoading(true);
    setTicketsError("");
    try {
      const { data } = await axios.get(
        `/api/account/my-tickets?includePast=${showPast}&includeQr=false`
      );
      setTickets(data || []);
    } catch (err) {
      setTicketsError(
        err?.response?.data?.message || "Failed to load your tickets."
      );
    } finally {
      setTicketsLoading(false);
    }
  };

  const generateAffiliateCode = async () => {
    try {
      setAffiliateLoading(true);
      const res = await axios.get("/api/affiliate/generate");
      setUser((prev) => ({ ...prev, affiliateCode: res.data.affiliateCode }));
    } catch {
      alert("Error generating affiliate code.");
    } finally {
      setAffiliateLoading(false);
    }
  };

  const clearAuthAndRedirect = () => {
    try {
      localStorage.removeItem("token");
      sessionStorage.removeItem("token");
    } catch {}
    navigate("/", { replace: true });
  };

  const handleDeleteAccount = async () => {
    setDeleteError("");
    setDeleting(true);
    try {
      await axios.delete("/api/account/delete");
      clearAuthAndRedirect();
    } catch (e) {
      setDeleteError(
        e?.response?.data || "Failed to delete your account. Please try again."
      );
      setDeleting(false);
    }
  };

  if (loadingUser) {
    return (
      <div className="page">
        <div className="card text-center">Loading…</div>
      </div>
    );
  }
  if (error || !user) {
    return (
      <div className="page">
        <div className="card text-amber-200 bg-amber-500/10 border-amber-400/20">
          {error || "No user info available."}
        </div>
      </div>
    );
  }

  const { username, email, birthDate, role, affiliateCode } = user;
  const canGenerateCode = ["ADMIN", "MASTER", "AFFILIATE"].includes(role);

  const deleteDisabled =
    deleting || !acknowledge || confirmText.trim().toUpperCase() !== "DELETE";

  // helpers
  const initials = (username || email || "?")
    .trim()
    .split(/\s+/)
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const isUnlimited =
    !!freeTicketUsage &&
    (
      freeTicketUsage.unlimited === true ||            // backend can send this
      freeTicketUsage.max === -1 ||                    // sentinel value
      (typeof freeTicketUsage.max === "number" && freeTicketUsage.max > 1000) || // absurdly large
      String(user?.role ?? "").toUpperCase() === "HEADLINER" ||                   // role equals Headliner
      ["subscriptionTier","tier","plan","membership"]                              // or tier field says Headliner
        .some(k => String(user?.[k] ?? "").toUpperCase().includes("HEADLINER"))
    );

  const usagePct =
    freeTicketUsage && freeTicketUsage.max > 0
      ? Math.min(100, Math.round((freeTicketUsage.used / freeTicketUsage.max) * 100))
      : 0;

  return (
    <div className="page space-y-6">
      {/* PROFILE HEADER */}
      <section className="card">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div className="flex items-center gap-4">
            <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-brand-500 to-glow grid place-items-center text-base font-semibold">
              {initials}
            </div>
            <div>
              <h1 className="text-xl md:text-2xl font-semibold text-white">
                {username ? `${username}` : "My Account"}
              </h1>
              <p className="text-slate-300 text-sm">{email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="badge">{role}</span>
            {birthDate && (
              <span className="badge">DOB: {birthDate}</span>
            )}
          </div>
        </div>
      </section>

      {/* GRID: FREE TICKETS + AFFILIATE */}
      <section className="grid gap-6 md:grid-cols-2">
        {/* FREE TICKET USAGE */}
        <div className="card">
          <h2 className="text-lg font-semibold text-white">Free Ticket Usage</h2>
          {!freeTicketUsage ? (
            <p className="mt-2 text-slate-300">No usage data available.</p>
          ) : (
            <>
              <p className="mt-2 text-slate-300">
                Used <strong className="text-white">{freeTicketUsage.used}</strong> of{" "}
                <strong className="text-white">
                  {isUnlimited ? "Unlimited" : freeTicketUsage.max}
                </strong>{" "}
                in {freeTicketUsage.year}.
              </p>

              {!isUnlimited ? (
                <>
                  <div className="mt-3 h-2 w-full rounded-full bg-white/10 overflow-hidden">
                    <div
                      className="h-2 bg-gradient-to-r from-brand-500 to-glow"
                      style={{ width: `${usagePct}%` }}
                    />
                  </div>
                  <p className="mt-2 text-sm text-slate-400">
                    <strong className="text-slate-200">{freeTicketUsage.remaining}</strong> remaining this year.
                  </p>
                </>
              ) : (
                <p className="mt-2 text-sm text-slate-400">
                  <strong className="text-slate-200">Unlimited</strong> remaining this year.
                </p>
              )}
            </>
          )}
        </div>

        {/* AFFILIATE */}
        {canGenerateCode && (
          <div className="card">
            <h2 className="text-lg font-semibold text-white">Affiliate Code</h2>
            {affiliateCode ? (
              <>
                <p className="mt-2">
                  <span className="text-slate-300">Current Code:</span>{" "}
                  <span className="font-mono text-white">{affiliateCode}</span>
                </p>
                <div className="mt-3 flex gap-2">
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => navigator.clipboard.writeText(affiliateCode)}
                  >
                    Copy Code
                  </button>
                  {/* Example referral link (uncomment if used) */}
                  {/* <button
                    type="button"
                    className="btn-secondary"
                    onClick={() =>
                      navigator.clipboard.writeText(`${window.location.origin}/register?ref=${affiliateCode}`)
                    }
                  >
                    Copy Referral Link
                  </button> */}
                </div>
              </>
            ) : (
              <button
                type="button"
                onClick={generateAffiliateCode}
                disabled={affiliateLoading}
                className="btn-primary mt-3"
              >
                {affiliateLoading ? "Generating…" : "Generate Affiliate Code"}
              </button>
            )}
          </div>
        )}
      </section>

      {/* TICKETS */}
      <section className="card">
        <div className="card-header">
          <h2 className="text-lg font-semibold text-white">My Tickets</h2>
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-300">Show past</span>
            <button
              type="button"
              onClick={() => setShowPast((v) => !v)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${
                showPast ? "bg-brand-600" : "bg-white/10"
              }`}
              aria-pressed={showPast}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white transition ${
                  showPast ? "translate-x-5" : "translate-x-1"
                }`}
              />
            </button>
          </div>
        </div>

        {ticketsLoading && <p className="text-slate-300">Loading tickets…</p>}
        {ticketsError && (
          <p className="text-red-300 bg-red-500/10 border border-red-500/20 rounded-xl px-3 py-2">
            {ticketsError}
          </p>
        )}

        {!ticketsLoading && !ticketsError && tickets.length === 0 && (
          <p className="text-slate-300">
            {showPast ? "No tickets found." : "No upcoming tickets yet."}
          </p>
        )}

        <ul className="mt-4 space-y-3">
          {tickets.map((t) => (
            <li key={t.ticketId} className="card-sm">
              <TicketCard ticket={t} />
            </li>
          ))}
        </ul>
      </section>

      {/* SECURITY */}
      <section className="grid gap-6 md:grid-cols-2">
        <ChangePasswordCard />
        <TwoFactorCard />
      </section>

      {/* DANGER ZONE */}
      <section className="card border-red-500/20 bg-red-500/5">
        <h2 className="text-lg font-semibold text-red-300">Danger Zone</h2>
        <p className="mt-2 text-sm text-red-200/90">
          Deleting your account will anonymise your profile and personal data. Some information
          required for compliance or payments may be retained per our policies. If you have any tickets for upcoming events
          the personal information on these tickets such as names and date of birth will not be deleted until the event has
          passed allowing you to still attend the events. If you would instead like this data removed immediatley please contact support
          at support@hey-lets-travel.com
        </p>
        <button
          onClick={() => setShowDeleteModal(true)}
          className="btn-primary mt-4 bg-red-600 hover:bg-red-700"
        >
          Delete &amp; Anonymise My Account
        </button>
      </section>

      {/* Delete confirmation modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4">
          <div className="card-solid max-w-lg w-full">
            <h3 className="text-xl font-semibold text-white">Confirm Account Deletion</h3>
            <p className="mt-2 text-sm text-slate-300">
              This action is <strong>irreversible</strong>. Type{" "}
              <code className="px-2 py-0.5 rounded bg-white/10 border border-white/10">DELETE</code> to confirm.
            </p>

            <div className="mt-4">
              <label className="label">Type DELETE to continue</label>
              <input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                className="input"
                placeholder="DELETE"
              />
            </div>

            <label className="mt-3 flex items-center gap-2 text-sm text-slate-300">
              <input
                type="checkbox"
                checked={acknowledge}
                onChange={(e) => setAcknowledge(e.target.checked)}
                className="rounded border-white/10 bg-white/5"
              />
              <span>I understand this cannot be undone.</span>
            </label>

            {deleteError && (
              <p className="mt-3 text-sm text-red-300 bg-red-500/10 border border-red-500/20 rounded-xl px-3 py-2">
                {deleteError}
              </p>
            )}

            <div className="mt-4 flex items-center justify-end gap-2">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setConfirmText("");
                  setAcknowledge(false);
                  setDeleting(false);
                  setDeleteError("");
                }}
                className="btn-secondary"
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={deleteDisabled}
                className={`btn-primary ${deleteDisabled ? "opacity-60 cursor-not-allowed" : "bg-red-600 hover:bg-red-700"}`}
              >
                {deleting ? "Deleting…" : "Delete my account"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** ---------- Ticket Card ---------- */
function TicketCard({ ticket }) {
  const [open, setOpen] = useState(false);
  const [details, setDetails] = useState(ticket);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const toggle = async () => {
    if (!open && !details.qrBase64) {
      setLoading(true);
      setErr("");
      try {
        const { data } = await axios.get(
          `/api/account/my-tickets/${ticket.ticketId}?includeQr=true`
        );
        setDetails(data);
      } catch (e) {
        setErr(e?.response?.data?.message || "Failed to load ticket details.");
      } finally {
        setLoading(false);
      }
    }
    setOpen(!open);
  };

  const price = Number(details.pricePaid || 0).toFixed(2);

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="font-semibold text-white">{details.eventTitle}</div>
          <div className="text-sm text-slate-300">
             {details.eventDate} • {details.startTime} – {details.endTime}
             {details.location && (
               <>
                {" "}•{" "}
                <span className="truncate inline-block max-w-[18rem]" title={details.location}>
                  {details.location}
                </span>
              </>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="badge">{details.ticketType}</span>
          <span className="badge">{details.status}</span>
          <button onClick={toggle} className="btn-secondary">
            {open ? "Hide details" : "View details"}
          </button>
        </div>
      </div>

      {open && (
        <div className="mt-4">
          {loading && <p className="text-slate-300">Loading details…</p>}
          {err && (
            <p className="text-red-300 bg-red-500/10 border border-red-500/20 rounded-xl px-3 py-2">
              {err}
            </p>
          )}
          {!loading && !err && (
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-1 text-slate-200">
                {details.location && (
                  <p><span className="text-slate-400">Location:</span> {details.location}</p>
                )}
                <p><span className="text-slate-400">Name:</span> {details.firstName} {details.lastName}</p>
                <p><span className="text-slate-400">DOB:</span> {details.dateOfBirth || "—"}</p>
                <p><span className="text-slate-400">Type:</span> {details.ticketType}</p>
                <p><span className="text-slate-400">Status:</span> {details.status}</p>
                <p><span className="text-slate-400">Price Paid:</span> £{price}</p>
                <p className="text-xs text-slate-400 mt-2">
                  Ticket ID: {details.ticketId} • Event ID: {details.eventId}
                </p>
              </div>
              <div className="flex items-center justify-center">
                {details.qrBase64 ? (
                  <img
                    src={`data:image/png;base64,${details.qrBase64}`}
                    alt="Ticket QR"
                    className="w-56 h-56 object-contain rounded-xl border border-white/10 bg-white/5 p-2"
                  />
                ) : (
                  <p className="text-slate-400">QR not available.</p>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** ---------- Two-Factor (TOTP) ---------- */
function TwoFactorCard() {
  const [status, setStatus] = useState({ enabled: false, hasSecret: false });
  const [qr, setQr] = useState(null);
  const [secret, setSecret] = useState("");
  const [code, setCode] = useState("");
  const [pw, setPw] = useState("");
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      const { data } = await axios.get("/api/account/2fa/status");
      setStatus(data);
    })();
  }, []);

  const startSetup = async () => {
    setMsg(""); setErr(""); setCode(""); setPw("");
    const { data } = await axios.post("/api/account/2fa/setup");
    setSecret(data.secretBase32);
    setQr(`data:image/png;base64,${data.qrBase64}`);
  };

  const enable = async () => {
    try {
      await axios.post("/api/account/2fa/enable", { code: Number(code) });
      setMsg("Two-factor enabled.");
      setQr(null); setSecret(""); setCode("");
      const { data } = await axios.get("/api/account/2fa/status");
      setStatus(data);
    } catch (e) {
      setErr(e?.response?.data || "Enable failed.");
    }
  };

  const disable = async () => {
    try {
      await axios.post("/api/account/2fa/disable", { password: pw, code: Number(code) });
      setMsg("Two-factor disabled.");
      setQr(null); setSecret(""); setCode(""); setPw("");
      const { data } = await axios.get("/api/account/2fa/status");
      setStatus(data);
    } catch (e) {
      setErr(e?.response?.data || "Disable failed.");
    }
  };

  return (
    <div className="card">
      <h2 className="text-lg font-semibold text-white">Two-Factor Authentication</h2>

      {msg && <div className="mt-3 px-3 py-2 rounded-xl border border-green-500/20 bg-green-500/10 text-green-200">{msg}</div>}
      {err && <div className="mt-3 px-3 py-2 rounded-xl border border-red-500/20 bg-red-500/10 text-red-200">{err}</div>}

      {!status.enabled ? (
        <div className="mt-3 space-y-3">
          {!qr ? (
            <button onClick={startSetup} className="btn-primary">Set up with Google Authenticator</button>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              <div className="flex items-center justify-center">
                <img src={qr} alt="Authenticator QR" className="rounded-xl border border-white/10 bg-white/5 p-2" />
              </div>
              <div className="min-w-0">
                <p className="text-slate-300 mb-2">Scan the QR or enter the secret manually:</p>
                <code className="block max-w-full break-all bg-white/10 border border-white/10 rounded px-2 py-1 text-slate-200">
                  {secret}
                </code>
                <div className="mt-4">
                  <label className="label">Enter 6-digit code</label>
                  <input className="input" value={code} onChange={(e)=>setCode(e.target.value)} />
                </div>
                <button onClick={enable} className="btn-primary mt-3">Enable</button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="mt-3 space-y-3">
          <p className="text-green-200">Two-factor is currently <strong>enabled</strong>.</p>
          <div className="grid md:grid-cols-2 gap-3 max-w-md">
            <input
              type="password"
              placeholder="Account password"
              className="input"
              value={pw}
              onChange={(e)=>setPw(e.target.value)}
            />
            <input
              placeholder="Current 6-digit code"
              className="input"
              value={code}
              onChange={(e)=>setCode(e.target.value)}
            />
          </div>
          <button onClick={disable} className="btn-secondary bg-red-600/90 hover:bg-red-700/90">Disable 2FA</button>
        </div>
      )}
    </div>
  );
}

/** ---------- Change Password ---------- */
function ChangePasswordCard() {
  const [currentPassword, setCurrent] = useState("");
  const [newPassword, setNewPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setMsg(""); setErr("");

    if (newPassword !== confirm) return setErr("Passwords do not match.");
    if (newPassword === currentPassword) {
    return setErr("New password must be different from your current password.");
  }
    try {
      await axios.post("/api/account/change-password", { currentPassword, newPassword });
      setMsg("Password changed.");
      setCurrent(""); setNewPw(""); setConfirm("");
    } catch (e) {
      setErr(e?.response?.data || "Failed to change password.");
    }
  };

  return (
    <div className="card">
      <h2 className="text-lg font-semibold text-white">Change Password</h2>
      {msg && <div className="mt-3 px-3 py-2 rounded-xl border border-green-500/20 bg-green-500/10 text-green-200">{msg}</div>}
      {err && <div className="mt-3 px-3 py-2 rounded-xl border border-red-500/20 bg-red-500/10 text-red-200">{err}</div>}
      <form onSubmit={submit} className="mt-3 grid gap-3 max-w-md">
        <input
          type="password"
          placeholder="Current password"
          className="input"
          value={currentPassword}
          onChange={(e)=>setCurrent(e.target.value)}
          required
        />
        <input
          type="password"
          placeholder="New password"
          className="input"
          value={newPassword}
          onChange={(e)=>setNewPw(e.target.value)}
          required
        />
        <input
          type="password"
          placeholder="Confirm new password"
          className="input"
          value={confirm}
          onChange={(e)=>setConfirm(e.target.value)}
          required
        />
        <button className="btn-primary">Update</button>
      </form>
      <p className="text-sm text-slate-400 mt-3">
        Forgot your current password?{" "}
        <Link to="/forgot-password" className="text-brand-500 hover:text-brand-600">Reset it here</Link>.
      </p>
    </div>
  );
}

export default Account;
